import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Directive, Input, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import * as i1$1 from '@angular/router';
import { TemplateAnnotations } from '@magnolia/template-annotations';
import { of, throwError } from 'rxjs';
import { map, exhaustMap, catchError } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i5 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/platform-browser';
import { MagnoliaModule } from '@magnolia/angular-editor';

class MagnoliaContentModel {
    constructor(page, templates) {
        this.pageData = page;
        this.templateDefinitions = templates;
    }
    getComponentsByArea(area) {
        const results = [];
        if (this.pageData[area]) {
            const areaNode = this.pageData[area];
            const components = areaNode['@nodes'];
            components.forEach((nodeName) => {
                const value = areaNode[nodeName];
                if (typeof value === 'object' && value['@nodeType'] === 'mgnl:component') {
                    results.push(value);
                }
            });
        }
        return results;
    }
    getPageData() {
        return this.pageData;
    }
    getResolverData() {
        return { page: this.pageData, templates: this.templateDefinitions };
    }
    // insideCmsEditor - not consumed by App
    getPageTemplate() {
        return this.pageData['mgnl:template'];
    }
    setTemplateDefinitions(data) {
        this.templateDefinitions = data;
    }
    getTemplateDefinitions() {
        return this.templateDefinitions;
    }
    getPageComment() {
        return TemplateAnnotations.getPageCommentString(this.pageData, this.getTemplateDefinitions()[this.getPageTemplate()]);
    }
    getAreaComment(area) {
        return TemplateAnnotations.getAreaCommentString(this.pageData[area], this.getTemplateDefinitions()[this.getPageTemplate()]);
    }
    getComponentComment(component) {
        return TemplateAnnotations.getComponentCommentString(component, this.getTemplateDefinitions()[component['mgnl:template']]);
    }
    // Currently unused - future lib api
    getPageTitle() {
        return this.pageData.title;
    }
    getAllAreas() {
        return this.pageData['@nodes'].filter((node) => !node.includes('0'));
    }
    getAreaContent(areaName) {
        return this.getPageData()[areaName];
    }
    getComponentTemplate(component) {
        return component['mgnl:template'];
    }
}

/** Service Injector Token required for SharedUiCmsModule.forRoot(config) */
const CmsConfigToken = new InjectionToken('CmsModuleConfig');

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/services/cms/cms.service.ts CmsService}
 */
class CmsApiService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
    }
    getPage(pagePath = '/') {
        const url = `${this.config.useCachedCmsOutput ? this.config.cachedRouteBase : this.config.restUrlBase}${pagePath}`;
        return this.http.get(url);
    }
    getTemplateDefinitions(template) {
        const url = `${this.config.templateDefinitionBase}/${template}`;
        return this.http.get(url);
    }
    getContentApp(path, params = null) {
        const query = params ? `?${params}` : '';
        const url = `${this.config.contentAppBase}${path}${query}`;
        return this.http.get(url);
    }
}
CmsApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsApiService, deps: [{ token: i1.HttpClient }, { token: CmsConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CmsApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsApiService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsApiService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CmsConfigToken]
                }] }]; } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/services/cms/cms.service.ts CmsService}
 */
class CmsDataAccessService {
    constructor(api, config) {
        this.api = api;
        this.config = config;
    }
    insideCmsEditor() {
        return this.config.insideCmsEditor;
    }
    getPageTitle(page) {
        return page.page.title;
    }
    getPage(pagePath) {
        return this.api.getPage(pagePath).pipe(map((data) => new MagnoliaContentModel(data)), exhaustMap((content) => this.insideCmsEditor()
            ? this.getTemplateDefinitions(content.getPageTemplate()).pipe(map((definitions) => {
                content.setTemplateDefinitions(definitions);
                return content.getResolverData();
            }))
            : of(content.getResolverData())), catchError(() => throwError('Error retrieving page content')));
    }
    getContentApp(path, params = null) {
        return this.api
            .getContentApp(path, params)
            .pipe(catchError(() => throwError('Error retrieving content app')));
    }
    getTemplateDefinitions(template) {
        return this.api
            .getTemplateDefinitions(template)
            .pipe(catchError(() => throwError('Error retrieving template definitions')));
    }
}
CmsDataAccessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsDataAccessService, deps: [{ token: CmsApiService }, { token: CmsConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CmsDataAccessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsDataAccessService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsDataAccessService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CmsApiService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CmsConfigToken]
                }] }]; } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/services/cms/cms.service.ts CmsService}
 */
class MgnlCmsMapperService {
    constructor(config) {
        this.config = config;
    }
    mgnlComponentsContentMapper(mgnlComponentsContent) {
        return mgnlComponentsContent.map((content) => {
            const image = content?.image
                ? {
                    ...content?.image,
                    url: this.config.damRawUrl + content.image['@link'],
                    name: content.image['@name'],
                }
                : undefined;
            return { ...content, image };
        });
    }
}
MgnlCmsMapperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlCmsMapperService, deps: [{ token: CmsConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
MgnlCmsMapperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlCmsMapperService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlCmsMapperService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CmsConfigToken]
                }] }]; } });

var ClosingComments;
(function (ClosingComments) {
    ClosingComments["AREA"] = "/cms:area";
    ClosingComments["COMPONENT"] = "/cms:component";
    ClosingComments["PAGE"] = "/cms:page";
})(ClosingComments || (ClosingComments = {}));

/**
 * @deprecated
 * Please use magnolia library elements
 */
class MagnoliaBase {
    constructor(_el, _renderer) {
        this._el = _el;
        this._renderer = _renderer;
    }
    insertComments(openingComment, closingComment) {
        const openComment = this._renderer.createComment(openingComment);
        const closeComment = this._renderer.createComment(closingComment);
        this._renderer.insertBefore(this._el.nativeElement, openComment, this._el.nativeElement.firstChild);
        this._renderer.appendChild(this._el.nativeElement, closeComment);
    }
}
MagnoliaBase.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaBase, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
MagnoliaBase.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: MagnoliaBase, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaBase, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; } });

/**
 * @deprecated
 * Please use magnolia library elements
 */
class MagnoliaAreaDirective extends MagnoliaBase {
    constructor(el, renderer, cms) {
        super(el, renderer);
        this.cms = cms;
    }
    ngOnInit() {
        if (this.cms.insideCmsEditor()) {
            const mgnlContent = this.content;
            const model = new MagnoliaContentModel(mgnlContent.page, mgnlContent.templates);
            this.insertComments(model.getAreaComment(this.area), ClosingComments.AREA);
        }
    }
}
MagnoliaAreaDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaAreaDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CmsDataAccessService }], target: i0.ɵɵFactoryTarget.Directive });
MagnoliaAreaDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: MagnoliaAreaDirective, selector: "[dgxMagnoliaArea]", inputs: { content: "content", area: "area" }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaAreaDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxMagnoliaArea]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CmsDataAccessService }]; }, propDecorators: { content: [{
                type: Input
            }], area: [{
                type: Input
            }] } });

/**
 * @deprecated
 * Please use magnolia library elements
 */
class MagnoliaComponentDirective extends MagnoliaBase {
    constructor(el, renderer, cms) {
        super(el, renderer);
        this.cms = cms;
    }
    ngOnInit() {
        if (this.cms.insideCmsEditor()) {
            const model = new MagnoliaContentModel(this.mgnlContent.page, this.mgnlContent.templates);
            this.insertComments(model.getComponentComment(this.component), ClosingComments.COMPONENT);
        }
    }
}
MagnoliaComponentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaComponentDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CmsDataAccessService }], target: i0.ɵɵFactoryTarget.Directive });
MagnoliaComponentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: MagnoliaComponentDirective, selector: "[dgxMagnoliaComponent]", inputs: { mgnlContent: "mgnlContent", component: "component" }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaComponentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxMagnoliaComponent]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CmsDataAccessService }]; }, propDecorators: { mgnlContent: [{
                type: Input
            }], component: [{
                type: Input
            }] } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/services/cms/cms.service.ts CmsService}
 */
class MgnlComponentService {
    constructor(cfr, config) {
        this.cfr = cfr;
        this.config = config;
    }
    loadComponent(vcr, mgnlComponent) {
        const mgnlComponentName = mgnlComponent['mgnl:template'];
        // clear the previous view
        vcr.clear();
        /**
         * 1. Get Angular component from magnolia.config
         * 2. Append the angular component to the container
         * 3. Pass the @Input value to the angular component
         */
        // 1.
        const angularComponent = this.config.componentMapping[mgnlComponentName];
        if (angularComponent) {
            // 2.
            const componentRef = vcr.createComponent(this.cfr.resolveComponentFactory(angularComponent));
            // 3.
            componentRef.instance.content = mgnlComponent;
        }
    }
}
MgnlComponentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlComponentService, deps: [{ token: i0.ComponentFactoryResolver }, { token: CmsConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
MgnlComponentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlComponentService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MgnlComponentService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CmsConfigToken]
                }] }]; } });

/**
 * @deprecated
 * Please use magnolia library elements
 */
class MagnoliaComponentHostDirective {
    constructor(_viewContainerRef, _mgnlComponentService) {
        this._viewContainerRef = _viewContainerRef;
        this._mgnlComponentService = _mgnlComponentService;
    }
    ngOnInit() {
        this._mgnlComponentService.loadComponent(this._viewContainerRef, this.component);
    }
}
MagnoliaComponentHostDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaComponentHostDirective, deps: [{ token: i0.ViewContainerRef }, { token: MgnlComponentService }], target: i0.ɵɵFactoryTarget.Directive });
MagnoliaComponentHostDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: MagnoliaComponentHostDirective, selector: "[dgxMgnlComponentHost]", inputs: { component: ["dgxMgnlComponentHost", "component"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MagnoliaComponentHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxMgnlComponentHost]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: MgnlComponentService }]; }, propDecorators: { component: [{
                type: Input,
                args: ['dgxMgnlComponentHost']
            }] } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/components/cms-page/cms-page.component.ts CmsPageComponent }.
 */
class CmsDynamicComponentOutletComponent {
    constructor(activatedRoute, mapper, cms) {
        this.activatedRoute = activatedRoute;
        this.mapper = mapper;
        this.cms = cms;
    }
    ngOnInit() {
        if (!this.activatedRoute.snapshot.data?.cmsContent) {
            throw new Error('<dgx-cms-dynamic-component-outlet> expected missing cmsContent property on resolver data');
        }
        this.mgnlContent = this.activatedRoute.snapshot.data.cmsContent;
        const model = new MagnoliaContentModel(this.mgnlContent.page, this.mgnlContent.templates);
        this.cmsComponentsContent = this.mapper.mgnlComponentsContentMapper(model.getComponentsByArea(this.area));
    }
    ngAfterViewInit() {
        // Needed to make child components appear editable in Magnolia page editor.
        // tslint:disable-next-line: no-string-literal
        const mgnlRefresh = window.parent['mgnlRefresh'];
        if (this.cms.insideCmsEditor() && mgnlRefresh) {
            mgnlRefresh();
        }
    }
}
CmsDynamicComponentOutletComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsDynamicComponentOutletComponent, deps: [{ token: i1$1.ActivatedRoute }, { token: MgnlCmsMapperService }, { token: CmsDataAccessService }], target: i0.ɵɵFactoryTarget.Component });
CmsDynamicComponentOutletComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: CmsDynamicComponentOutletComponent, selector: "dgx-cms-dynamic-component-outlet", inputs: { area: "area" }, ngImport: i0, template: "<div dgxMagnoliaArea [content]=\"mgnlContent\" [area]=\"area\">\n  <ng-template ngFor let-cmsComponent [ngForOf]=\"cmsComponentsContent\">\n    <div dgxMagnoliaComponent [mgnlContent]=\"mgnlContent\" [component]=\"cmsComponent\"></div>\n    <ng-template *dgxMgnlComponentHost=\"cmsComponent\"></ng-template>\n  </ng-template>\n</div>\n", styles: [""], directives: [{ type: MagnoliaAreaDirective, selector: "[dgxMagnoliaArea]", inputs: ["content", "area"] }, { type: i5.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: MagnoliaComponentDirective, selector: "[dgxMagnoliaComponent]", inputs: ["mgnlContent", "component"] }, { type: MagnoliaComponentHostDirective, selector: "[dgxMgnlComponentHost]", inputs: ["dgxMgnlComponentHost"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsDynamicComponentOutletComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dgx-cms-dynamic-component-outlet', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div dgxMagnoliaArea [content]=\"mgnlContent\" [area]=\"area\">\n  <ng-template ngFor let-cmsComponent [ngForOf]=\"cmsComponentsContent\">\n    <div dgxMagnoliaComponent [mgnlContent]=\"mgnlContent\" [component]=\"cmsComponent\"></div>\n    <ng-template *dgxMgnlComponentHost=\"cmsComponent\"></ng-template>\n  </ng-template>\n</div>\n", styles: [""] }]
        }], ctorParameters: function () { return [{ type: i1$1.ActivatedRoute }, { type: MgnlCmsMapperService }, { type: CmsDataAccessService }]; }, propDecorators: { area: [{
                type: Input
            }] } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/components/cms-page/cms-page.component.ts CmsPageComponent }.
 */
class CmsPageDirective extends MagnoliaBase {
    constructor(activatedRoute, title, el, renderer, cms) {
        super(el, renderer);
        this.activatedRoute = activatedRoute;
        this.title = title;
        this.cms = cms;
    }
    ngOnInit() {
        if (!this.activatedRoute.snapshot.data?.cmsContent) {
            throw new Error('cmsPage directive expected missing cmsContent property on resolver data');
        }
        const mgnlContent = this.activatedRoute.snapshot.data.cmsContent;
        this.title.setTitle(this.cms.getPageTitle(mgnlContent));
        if (this.cms.insideCmsEditor()) {
            const model = new MagnoliaContentModel(mgnlContent.page, mgnlContent.templates);
            this.insertComments(model.getPageComment(), ClosingComments.PAGE);
        }
    }
}
CmsPageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsPageDirective, deps: [{ token: i1$1.ActivatedRoute }, { token: i2.Title }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CmsDataAccessService }], target: i0.ɵɵFactoryTarget.Directive });
CmsPageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: CmsPageDirective, selector: "[dgxCmsPage]", usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsPageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxCmsPage]',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.ActivatedRoute }, { type: i2.Title }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CmsDataAccessService }]; } });

class CmsPageContentResolver {
    constructor(cmsService, config) {
        this.cmsService = cmsService;
        this.config = config;
    }
    resolve(route, state) {
        // default pagePath to pageRoot
        const pageRoot = this.config.pagesRoot;
        const customCmsUrl = state.root.root.children[0].data?.cmsPagePath;
        let pagePath;
        if (this.config.useCachedCmsOutput) {
            /**
             * TODO: this will likely need to be adapted to allow customCmsUrl
             * but the node caching script will need to adjust at the same time
             */
            pagePath = `${state.url}.json`;
        }
        else {
            pagePath =
                state.url === '/' ? pageRoot : `${pageRoot}${customCmsUrl ? customCmsUrl : state.url}`;
        }
        return this.cmsService.getPage(pagePath);
    }
}
CmsPageContentResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsPageContentResolver, deps: [{ token: CmsDataAccessService }, { token: CmsConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CmsPageContentResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsPageContentResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CmsPageContentResolver, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: CmsDataAccessService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CmsConfigToken]
                }] }]; } });

/**
 * @deprecated
 * Please use
 * {@link https://github.com/domgen/dgx-components/blob/develop/libs/cms/src/lib/cms.module.ts DgxCmsModule }.
 */
class SharedUiCmsModule {
    static forRoot(token) {
        return {
            ngModule: SharedUiCmsModule,
            providers: [
                CmsApiService,
                MgnlComponentService,
                {
                    provide: CmsConfigToken,
                    // ES6 object method shorthand to bypass AOT issue in libs useFactory
                    // https://github.com/angular/angular/issues/13614
                    useFactory(runtimeConfig) {
                        return runtimeConfig;
                    },
                    deps: [token],
                },
            ],
        };
    }
}
SharedUiCmsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SharedUiCmsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SharedUiCmsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SharedUiCmsModule, declarations: [MagnoliaComponentHostDirective,
        MagnoliaAreaDirective,
        MagnoliaComponentDirective,
        CmsPageDirective,
        CmsDynamicComponentOutletComponent], imports: [CommonModule, MagnoliaModule], exports: [CmsPageDirective,
        CmsDynamicComponentOutletComponent] });
SharedUiCmsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SharedUiCmsModule, providers: [CmsApiService, MgnlComponentService, CmsDataAccessService, MgnlCmsMapperService], imports: [[CommonModule, MagnoliaModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SharedUiCmsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        MagnoliaComponentHostDirective,
                        MagnoliaAreaDirective,
                        MagnoliaComponentDirective,
                        CmsPageDirective,
                        CmsDynamicComponentOutletComponent,
                    ],
                    imports: [CommonModule, MagnoliaModule],
                    exports: [
                        CmsPageDirective,
                        CmsDynamicComponentOutletComponent,
                        /* TODO: expose these directives if there is a use case for them individulally
                        currently hidden from public api because they're Magnolia specific
                        MagnoliaAreaDirective,
                        MagnoliaComponentDirective,
                        MagnoliaComponentHostDirective, */
                    ],
                    providers: [CmsApiService, MgnlComponentService, CmsDataAccessService, MgnlCmsMapperService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CmsConfigToken, CmsDataAccessService, CmsDynamicComponentOutletComponent, CmsPageContentResolver, CmsPageDirective, SharedUiCmsModule };
